import { useCallback, useEffect } from "react";
import { messageService } from "../services";
import type { UseMessagesProps } from "../types/messages";
import { useMessageManagement } from "./useMessageManagement";
import { useStreamProcessing } from "./useStreamProcessing";
import { useThreadManagement } from "./useThreadManagement";
import { useToolCallHandling } from "./useToolCallHandling";
import { isAssistantMessage } from "../component/types/message";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import { FEATURE_FLAG } from "ee/entities/FeatureFlag";

export function useMessages({
  activeEnv,
  initialAssistantMessage,
  initialAssistantSuggestions,
  logError,
  logEvent,
  queryId,
  startOperation,
  state,
  stopOperation,
  updateState,
  viewMode,
  widgetId,
}: UseMessagesProps) {
  const {
    appendMessage,
    approveToolCall,
    getErrorAssistantMessage,
    getInitialAssistantMessage,
    handlePromptChange,
    messages,
    prompt,
    rejectToolCall,
    setMessages,
    setPrompt,
  } = useMessageManagement({
    initialAssistantMessage,
    initialAssistantSuggestions,
    logEvent,
  });

  const { updateFromStream } = useStreamProcessing({
    updateState,
    appendMessage,
    getErrorAssistantMessage,
    setMessages,
    logError,
    stopOperation,
  });

  const { createNewThread, getMessages, getThreads } = useThreadManagement({
    state,
    updateState,
    widgetId,
    queryId,
    getInitialAssistantMessage,
    getErrorAssistantMessage,
    setMessages,
    initialAssistantMessage,
    initialAssistantSuggestions,
    logError,
  });

  useToolCallHandling({
    messages,
    setMessages,
    updateState,
    startOperation,
    updateFromStream,
    widgetId,
    queryId,
    activeEnv,
    viewMode,
    logError,
    isCancelling: state.isCancelling,
    stopOperation,
  });

  const sendMessage = useCallback(async () => {
    if (!prompt.trim()) return;

    // get the latest assistant message
    const lastAssistantMessage = [...messages]
      .reverse()
      .find(isAssistantMessage);

    appendMessage({
      createdAt: new Date(),
      id: Math.random().toString(),
      content: prompt,
      role: "user",
      type: "text",
    });

    setPrompt("");
    updateState({ isWaitingForResponse: true });

    logEvent("INVOKE_AI_CHAT");

    const signal = startOperation();

    try {
      const response = await messageService.sendMessage(
        {
          threadId: state.threadId,
          widgetId,
          message: prompt,
          activeEnv,
          queryId,
          viewMode,
          prevResponseId: lastAssistantMessage?.responseId || undefined,
        },
        signal,
      );

      await updateFromStream(response, signal);
    } catch (error) {
      if (signal.aborted) {
        // User cancelled - we don't need to show an error
        return;
      }

      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";

      logError(errorMessage);

      appendMessage(getErrorAssistantMessage(errorMessage));
    } finally {
      stopOperation();
    }
  }, [
    prompt,
    messages,
    appendMessage,
    setPrompt,
    updateState,
    logEvent,
    startOperation,
    state.threadId,
    widgetId,
    activeEnv,
    queryId,
    viewMode,
    updateFromStream,
    logError,
    getErrorAssistantMessage,
    stopOperation,
  ]);

  /**
   *  CANCEL RUN
   */

  const isResponsesAPIActive = useFeatureFlag(
    FEATURE_FLAG.license_responses_api_enabled,
  );

  const cancelRun = useCallback(() => {
    updateState({ isCancelling: true });
  }, [updateState]);

  const onCancelRun = useCallback(async () => {
    if (isResponsesAPIActive || state.runId) {
      stopOperation();
      await messageService.cancelRun(
        queryId,
        state.threadId,
        state.runId,
        viewMode,
      );
      updateState({ isCancelling: false, runId: "" });
    }
  }, [
    isResponsesAPIActive,
    state.runId,
    state.threadId,
    stopOperation,
    queryId,
    viewMode,
    updateState,
  ]);

  useEffect(
    function handleCanceling() {
      // If the new API is active, we dont need the runId to cancel the run
      if (state.isCancelling && (isResponsesAPIActive || state.runId)) {
        onCancelRun();
      }
    },
    [state.isCancelling, onCancelRun, isResponsesAPIActive, state.runId],
  );

  return {
    messages,
    prompt,
    getThreads,
    getMessages,
    createNewThread,
    sendMessage,
    cancelRun,
    approveToolCall,
    rejectToolCall,
    handlePromptChange,
  };
}
